/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import React, { useState } from 'react';
import { OnChangeEvent, RadioEvent } from '../events/EventProvider';
import useEvents from '../events/useEvents';
import Label from '../Label/Label';
import useTheme from '../style/useTheme';
import { getClassNames, hex2rgba, getGetIdFn } from '../utils';
import BaseRadio from './BaseRadio';
import InteractiveDiv from '../InteractiveDiv/InteractiveDiv';

export type Props = {
  checked: boolean;
  onChange?: (value: string) => void;
  label?: string;
  value: string;
  ariaLabel?: string;
  eventId?: string;
};

const getLabelId = getGetIdFn('Label');

const Radio: React.FC<React.PropsWithChildren<Props>> = ({
  checked,
  onChange,
  label,
  value,
  ariaLabel,
  children,
  eventId,
}) => {
  const { theme, resolveColor } = useTheme();
  const id = getLabelId();

  const triggerEvent = useEvents<OnChangeEvent<RadioEvent>>(eventId, 'Radio');
  const [focused, setFocused] = useState(false);

  return (
    <InteractiveDiv
      role="radio"
      aria-checked={checked}
      onMouseOver={() => setFocused(true)}
      onFocus={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      css={{
        padding: '16px',
        display: 'flex',
        borderRadius: '8px',
        transition: `all ${theme.animations.duration.standard}ms ${theme.animations.easing.easeOut}`,
        flexDirection: 'column',
        alignItems: 'flex-start',
        cursor: 'pointer',
        '&:hover': {
          background: hex2rgba(
            resolveColor(theme.components.radio['selected-background']),
            0.04
          ),
        },
        backgroundColor: checked
          ? hex2rgba(
              resolveColor(theme.components.radio['selected-background']),
              0.08
            )
          : 'transparent',
      }}
      data-testid={`Radio-${value}`}
      aria-label={ariaLabel}
      className={getClassNames(
        { 'origami-Radio--selected': checked },
        'origami-Radio'
      )}
      onClick={() => {
        if (!checked && onChange) {
          onChange(value);
          triggerEvent({ value, checked: !checked });
        }
      }}
    >
      <BaseRadio focused={focused} checked={checked}>
        <Label
          label={label || value}
          htmlFor={id}
          cursor="pointer"
          margin="2px 0"
        />
      </BaseRadio>
      {children}
    </InteractiveDiv>
  );
};

export default Radio;
